import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

import { 
  languageList, 
  getTranslation, 
  isRTL, 
  loadLanguage, 
  isLanguageLoaded,
  fullTranslations 
} from '../i18n/translations100';

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(
    localStorage.getItem('rostafy_language') || 'en'
  );
  const [isLoading, setIsLoading] = useState(false);

  // Load language translations on demand
  const setLanguage = useCallback(async (lang) => {
    if (!languageList[lang]) {
      console.warn(`Language ${lang} not supported, falling back to English`);
      lang = 'en';
    }

    // Load language if not already loaded
    if (!isLanguageLoaded(lang)) {
      setIsLoading(true);
      try {
        await loadLanguage(lang);
      } catch (error) {
        console.error(`Failed to load language ${lang}:`, error);
      }
      setIsLoading(false);
    }

    setLanguageState(lang);
    localStorage.setItem('rostafy_language', lang);
  }, []);

  // Initial language load
  useEffect(() => {
    const initLanguage = async () => {
      const savedLang = localStorage.getItem('rostafy_language') || 'en';
      if (!isLanguageLoaded(savedLang) && savedLang !== 'en' && savedLang !== 'es') {
        setIsLoading(true);
        try {
          await loadLanguage(savedLang);
        } catch (error) {
          console.error(`Failed to load initial language ${savedLang}:`, error);
        }
        setIsLoading(false);
      }
    };
    initLanguage();
  }, []);

  // Update document direction and lang attribute
  useEffect(() => {
    document.documentElement.dir = isRTL(language) ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  // Translation function with fallback
  const t = useCallback((key) => getTranslation(language, key), [language]);

  // Get all available languages
  const languages = Object.keys(languageList);
  
  // Language names for selector
  const languageNames = Object.fromEntries(
    Object.entries(languageList).map(([code, info]) => [code, info.native])
  );

  const value = {
    language,
    setLanguage,
    t,
    languages,
    languageNames,
    languageList,
    isRTL: isRTL(language),
    isLoading,
    translations: fullTranslations
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
